import { Injectable, inject } from '@angular/core';
import { Auth, signInWithCustomToken, signOut } from '@angular/fire/auth';
import { HttpClient, HttpContext } from '@angular/common/http';
import { Observable, from, of } from 'rxjs';
import { catchError, map, switchMap, tap } from 'rxjs/operators';
import { patientBackendEndPointToken } from '../../app.config';
import { DisableCache, LoadingUIEnabled } from '../../loading.interceptor';

export interface FirebaseTokenResponse {
  firebaseToken: string;
  uid: string;
}

@Injectable({ providedIn: 'root' })
export class FirebaseAuthService {
  private readonly auth = inject(Auth, { optional: true });
  private readonly http = inject(HttpClient);
  private readonly backendApi = inject(patientBackendEndPointToken);
  private readonly apiBase = `${this.backendApi.replace(/\/fhir\/?$/, '')}`;

  /**
   * Request a custom Firebase token from nodejsProxy and silently sign into Firebase Auth.
   */
  syncFirebaseAuth(): Observable<boolean> {
    if (!this.auth) {
      console.warn('[FirebaseAuth] Firebase Auth not initialized, skipping custom token sync.');
      return of(false);
    }

    return this.http.post<FirebaseTokenResponse>(
      `${this.apiBase}/auth/firebase-token`,
      {},
      {
        context: new HttpContext().set(DisableCache, true).set(LoadingUIEnabled, false)
      }
    ).pipe(
      switchMap((res) => {
        if (!res?.firebaseToken) {
          console.warn('[FirebaseAuth] No firebaseToken returned from server.');
          return of(false);
        }

        console.log(`[FirebaseAuth] Received custom token for uid=${res.uid}. Signing into Firebase Auth...`);
        return from(signInWithCustomToken(this.auth!, res.firebaseToken)).pipe(
          tap((cred) => {
            console.log(`[FirebaseAuth] Successfully signed into Firebase Auth as uid=${cred.user.uid}`);
          }),
          map(() => true),
          catchError((err) => {
            console.error('[FirebaseAuth] signInWithCustomToken failed:', err);
            return of(false);
          })
        );
      }),
      catchError((err) => {
        console.warn('[FirebaseAuth] Failed to obtain Firebase custom token from proxy:', err?.message || err);
        return of(false);
      })
    );
  }

  /**
   * Sign out of Firebase Auth
   */
  signOut(): Promise<void> {
    if (!this.auth) {
      return Promise.resolve();
    }
    return signOut(this.auth).catch((err) => {
      console.warn('[FirebaseAuth] signOut error:', err);
    });
  }

  /**
   * Check if Firebase is currently authenticated
   */
  get isAuthenticated(): boolean {
    return Boolean(this.auth?.currentUser);
  }

  /**
   * Current Firebase user UID
   */
  get currentUid(): string | null {
    return this.auth?.currentUser?.uid ?? null;
  }
}
